import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import noScroll from 'no-scroll';
import classes from './styles/index.module.scss';
import { bodyOverlayPositionActiveClass, overlayPositionPortalClass } from './overlayPositionDefaults';
import $ from 'jquery';
import debounce from 'lodash/debounce';
import sortBy from 'lodash/sortBy';
import remove from 'lodash/remove';
import { togglePositionOverlay } from '../../../modules/app/store';
import { modalBoxUtils } from '../../../components/ModalBox';

const MAX_ALLOW_PIXELS_DIFFERENCE = 3;
export const mapDispatch = (dispatch) =>
	bindActionCreators(
		{
			togglePositionOverlay,
		},
		dispatch,
	);

export class OverlayPosition extends Component {
	syncState = {
		containerPortal: null,
		refs: {
			overlayPosition: createRef(),
			overlayPositionComponent: createRef(),
		},
		onWindowResize: null,
		hasActiveNoScrollPluginBeforeMount: false,
		prevClientHeight: null,
		prevClientWidth: null,
		overlayPositionComponentResizeObserver: null,
	};

	state = {
		isMounted: false,
	};

	constructor(props) {
		super(props);

		let containerPortal = document.querySelector(`.${overlayPositionPortalClass}`);

		if (containerPortal === null) {
			containerPortal = document.createElement('div');
			containerPortal.classList.add(overlayPositionPortalClass);

			document.body.appendChild(containerPortal);
		}

		this.syncState.containerPortal = containerPortal;
		this.syncState.onWindowResize = debounce(this.handleWindowResize, 300);
	}

	get shouldDetachNoScrollPlugin() {
		return !this.syncState.hasActiveNoScrollPluginBeforeMount && !modalBoxUtils.isOpen();
	}

	get shouldForceUpdate() {
		const { prevClientHeight, prevClientWidth } = this.syncState;
		const { scrollHeight, clientWidth } = this.overlayPositionComponent;
		const boxModelChanged = prevClientHeight !== scrollHeight || prevClientWidth !== clientWidth;

		if (!boxModelChanged) {
			return false;
		}

		const needUpdateByWidth = Math.abs(prevClientWidth - clientWidth) > MAX_ALLOW_PIXELS_DIFFERENCE;
		const needUpdateByHeight = Math.abs(prevClientHeight - scrollHeight) > MAX_ALLOW_PIXELS_DIFFERENCE;

		return needUpdateByHeight || needUpdateByWidth;
	}

	get callerComponent() {
		return this.props.callerComponentRef.current;
	}

	get overlayPosition() {
		return this.syncState.refs.overlayPosition.current;
	}

	get overlayPositionComponent() {
		return this.syncState.refs.overlayPositionComponent.current;
	}

	get align() {
		const splittedPosition = this.props.position.toLowerCase().split('_');

		return splittedPosition.length > 1 ? splittedPosition[1] : splittedPosition[0];
	}

	get callerComponentClientRect() {
		const callerComponent = this.callerComponent;
		const boundingClientRect = callerComponent.getBoundingClientRect();

		return {
			...$(callerComponent).offset(),
			height: Math.round(boundingClientRect.height),
			width: Math.round(boundingClientRect.width),
		};
	}

	get bottomPosition() {
		const { callerComponentClientRect } = this;

		return callerComponentClientRect.top + callerComponentClientRect.height;
	}

	get topPosition() {
		return this.callerComponentClientRect.top - this.overlayPositionComponent.scrollHeight;
	}

	get startAlign() {
		return this.callerComponentClientRect.left;
	}

	get endAlign() {
		const { callerComponentClientRect } = this;

		return (
			callerComponentClientRect.left + callerComponentClientRect.width - this.overlayPositionComponent.clientWidth
		);
	}

	get fitBottomPosition() {
		const bottom = this.bottomPosition + this.overlayPositionComponent.scrollHeight;
		return this.overlayPosition.clientHeight > bottom;
	}

	get fitTopPosition() {
		return this.topPosition > 0;
	}

	get fitStartAlign() {
		return this.startAlign + this.overlayPositionComponent.clientWidth <= this.overlayPosition.clientWidth;
	}

	get fitEndAlign() {
		return this.endAlign >= 0;
	}

	get fitStretch() {
		return this.fitStartAlign && this.fitEndAlign;
	}

	get fitCoordinates() {
		const overlayPositionComponentHeight = this.overlayPositionComponent.scrollHeight;
		const overlayPositionHeight = this.overlayPosition.clientHeight;
		let bottomCoordinates = this.bottomPosition + overlayPositionComponentHeight;
		const { align } = this;
		while (bottomCoordinates > overlayPositionHeight) {
			bottomCoordinates -= 5;
		}

		let left = 0;

		if (align === 'end' && this.fitEndAlign) {
			left = this.endAlign;
		}

		if ((align === 'start' || align === 'stretch') && this.fitStartAlign) {
			left = this.startAlign;
		}

		return {
			top: bottomCoordinates - overlayPositionComponentHeight,
			left,
		};
	}

	get sortedPossiblePositions() {
		const { possiblePositions } = this.props;

		return sortBy(possiblePositions, (v) => v.indexOf(this.align) === -1);
	}

	componentDidMount() {
		this.syncState.hasActiveNoScrollPluginBeforeMount = noScroll.isOn;

		if (!noScroll.isOn) {
			noScroll.on();
		}

		document.body.classList.add(bodyOverlayPositionActiveClass);
		window.addEventListener('resize', this.syncState.onWindowResize);

		this.setState({
			isMounted: true,
		});

		window.__REACT__OVERLAY_POSITION_TS = this.updatePosition;
		this.syncState.prevClientHeight = this.overlayPositionComponent.scrollHeight;
		this.syncState.prevClientWidth = this.overlayPositionComponent.clientWidth;

		this.props.togglePositionOverlay(true);

		this.syncState.overlayPositionComponentResizeObserver = new ResizeObserver((entries) => {
			for (let entry of entries) {
				const height = entry.target.scrollHeight;
				const width = entry.target.clientWidth;

				if (this.shouldForceUpdate) {
					this.forceUpdate();
				}

				this.syncState.prevClientHeight = height;
				this.syncState.prevClientWidth = width;
			}
		});

		this.syncState.overlayPositionComponentResizeObserver.observe(this.overlayPositionComponent);
	}

	componentWillUnmount() {
		if (this.shouldDetachNoScrollPlugin) {
			noScroll.off();
		}

		document.body.classList.remove(bodyOverlayPositionActiveClass);
		window.removeEventListener('resize', this.syncState.onWindowResize);

		this.setState({
			isMounted: false,
		});
		this.props.togglePositionOverlay(false);
		window.__REACT__OVERLAY_POSITION_TS = null;
		this.syncState.overlayPositionComponentResizeObserver.disconnect();
		this.syncState.overlayPositionComponentResizeObserver = null;
	}

	getAllowPosition(position, possiblePositions = [...this.sortedPossiblePositions]) {
		position = position.toLowerCase();
		remove(possiblePositions, (p) => p === position);

		switch (position) {
			case 'bottom_start':
				if (this.fitBottomPosition && this.fitStartAlign) {
					return 'bottom_start';
				}
				break;
			case 'bottom_end':
				if (this.fitBottomPosition && this.fitEndAlign) {
					return 'bottom_end';
				}
				break;
			case 'top_start':
				if (this.fitTopPosition && this.fitStartAlign) {
					return 'top_start';
				}
				break;
			case 'top_end':
				if (this.fitTopPosition && this.fitEndAlign) {
					return 'top_end';
				}
				break;
			case 'bottom_stretch':
				if (this.fitBottomPosition && this.fitStretch) {
					return 'bottom_start';
				}
				break;
			case 'top_stretch':
				if (this.fitTopPosition && this.fitStretch) {
					return 'top_start';
				}
				break;
		}

		const nextPosition = possiblePositions.shift();

		if (!nextPosition) return 'fit';

		return this.getAllowPosition(nextPosition, possiblePositions);
	}

	getPositionCoordinates(position) {
		switch (position) {
			case 'bottom_start':
				return {
					top: this.bottomPosition,
					left: this.startAlign,
				};
			case 'bottom_end':
				return {
					top: this.bottomPosition,
					left: this.endAlign,
				};
			case 'top_start':
				return {
					top: this.topPosition,
					left: this.startAlign,
				};
			case 'top_end':
				return {
					top: this.topPosition,
					left: this.endAlign,
				};

			case 'fit':
				const coordinates = this.fitCoordinates;

				if (position === 'fit' && coordinates.top < 0) {
					coordinates.top = 0;
					coordinates.maxHeight = `${this.overlayPosition.clientHeight}px`;
					coordinates.overflowY = 'scroll';
				}

				return coordinates;
		}
	}

	handleWindowResize = () => {
		this.forceUpdate();
	};

	updatePosition = (props = {}) => {
		if (props.forceUpdate) {
			this.forceUpdate();

			return;
		}

		if (this.shouldForceUpdate) {
			this.forceUpdate();
		}
	};

	render() {
		let styles = {
			top: 0,
			left: 0,
		};

		if (this.state.isMounted) {
			const allowPosition = this.getAllowPosition(this.props.position);
			styles = this.getPositionCoordinates(allowPosition);
		}

		return createPortal(
			<div
				onClick={() => this.props.close && this.props.close()}
				className={classes.overlayPosition}
				ref={this.syncState.refs.overlayPosition}
			>
				<div
					className={classes.overlayPositionComponent}
					ref={this.syncState.refs.overlayPositionComponent}
					style={styles}
				>
					{this.props.render(styles)}
				</div>
			</div>,
			this.syncState.containerPortal,
		);
	}
}

OverlayPosition.propTypes = {
	callerComponentRef: PropTypes.any,
	position: PropTypes.string,
	possiblePositions: PropTypes.arrayOf(PropTypes.string),
};

OverlayPosition.defaultProps = {
	position: 'bottom_stretch',
	possiblePositions: ['top_stretch', 'bottom_stretch'],
};

export const ConnectedOverlayPosition = connect(null, mapDispatch)(OverlayPosition);
